import { FlyoutListMenuWidget, MenuButtonWidget, MenuItemInfo } from '@tableau/widgets';
import React from 'react';

import { openReactConfirmationDialog } from '~commonInfra/dialogs/ReactConfirmationDialog';
import { Messages } from '~src/codegen/Localize';
import { TabDialogWidth } from '~src/commonInfra/dialogs/style/TabDialogWidth';
import { ConnectedApplications } from '~src/connectedApplications/resources/ConnectedApplicationsResource';
import { IResult } from '~src/httpClient/serverApi/types/IResponse';

interface SecretDisplayBoxAction {
    label: string;
    performAction: () => void;
    isDisabled: boolean;
}

export interface SecretDisplayBoxActionButtonProps {
    appId: string;
    secretId: string;
    secretValue: string;
    rerenderCallback: (secretId: string) => void;
}

export interface SecretDisplayBoxActionButtonState {
    isOpen: boolean;
}

export class SecretDisplayBoxActionButton extends React.Component<SecretDisplayBoxActionButtonProps, SecretDisplayBoxActionButtonState> {
    public constructor(props: SecretDisplayBoxActionButtonProps) {
        super(props);
        this.onSelect = this.onSelect.bind(this);
        this.deleteAction = this.deleteAction.bind(this);
        this.state = { isOpen: false };
    }

    private menuId: string = `action-button`;
    private buttonElement: HTMLElement | null = null;

    private menuItems: MenuItemInfo[] = [
        { value: 'deleteSecret', displayValue: Messages.connectedApplicationsDeleteSecret() },
        { value: 'copyId', displayValue: Messages.connectedApplicationsCopySecretId() },
        { value: 'copyKey', displayValue: Messages.connectedApplicationsCopySecretValue() },
    ];

    private async deleteAction(): Promise<IResult> {
        let result = await ConnectedApplications.deleteSecret(this.props.appId, this.props.secretId);
        if(result !== undefined && !result.errors){
            this.props.rerenderCallback(this.props.secretId);
        }
        return result;
    }

    private actions: SecretDisplayBoxAction[] = [
        {
            label: 'deleteSecret',
            performAction: () => openReactConfirmationDialog<void, IResult>({
                title: Messages.connectedApplicationsDeleteSecret(),
                message: Messages.connectedApplicationsDeleteSecretConfirmationMessage(),
                paramsForAction: undefined,
                actionName: Messages.deleteMessage(),
                action: () => { return this.deleteAction(); },
                isActionDelete: true,
                dialogWidth: TabDialogWidth.width375px
            }),
            isDisabled: false,
        },
        {
            label: 'copyId',
            performAction: async () => await navigator.clipboard.writeText(this.props.secretId),
            isDisabled: false,
        },
        {
            label: 'copyKey',
            performAction: async () => await navigator.clipboard.writeText(this.props.secretValue),
            isDisabled: false,
        },
    ];

    private setButtonRef = (ref: HTMLButtonElement | null): void => {
        this.buttonElement = ref;
    };

    protected onSelect(value: string) {
        this.setState({ isOpen: false });

        const action = this.actions.find(x => x.label === value);
        action.performAction();
    }

    public render(): JSX.Element {
        const buttonStyle: React.CSSProperties = {
            paddingLeft: '24px',
            paddingRight: '24px',
            paddingTop: '5px',
            paddingBottom: '5px'
        };

        return <>
            <MenuButtonWidget
                style={buttonStyle}
                aria-controls={this.menuId}
                isOpen={this.state.isOpen}
                onClick={() => this.setState({ isOpen: !this.state.isOpen })}
                ref={this.setButtonRef}
                testId='connected-applications-secret-action-button'
                children={Messages.connectedApplicationsSecretActionButtonTitle()}
            />
            {this.state.isOpen && <FlyoutListMenuWidget
                anchorEl={this.buttonElement}
                allowAutoPosition
                id={this.menuId}
                menuItemInfos={this.menuItems}
                onSelect={this.onSelect}
                requestCloseAll={() => this.setState({ isOpen: false })}
                menuStyle={{ minWidth: this.buttonElement.getBoundingClientRect().width }}
                subMenuAllowAutoPosition
                testId='connected-applications-secret-action-list'
                toggleEl={this.buttonElement!}
            />}
        </>;
    }
}
